import { useCallback, useEffect, useRef, useState } from "react";

import {
  reducePregameAction,
  type PregameDocument as DomainPregameDocument,
  type PregamePlayer,
} from "../../shared/domain/pregameReducer";
import type {
  PlayerListItem,
  PregameAction,
  PregameViewModel,
  RemoteTimestamp,
} from "../shared/types/contracts";

type ProjectedPregame = DomainPregameDocument & { revision: number };

export interface PregameProjectionHandle {
  acknowledgement: Promise<void> | null;
}

interface PregameProjection {
  actionId: string;
  action: PregameAction;
  fallback: ProjectedPregame;
  jersey: string | null;
  projectsJersey: boolean;
  committedRevision: number | null;
  responseTeamSequence: number | null;
  pregameAcknowledged: boolean;
  teamAcknowledged: boolean;
  acknowledgement: Promise<void>;
  resolveAcknowledgement: () => void;
}

interface OptimisticPregameOptions {
  scopeKey: string;
  pregame: PregameViewModel;
  players: PlayerListItem[];
  serverJerseyHolderPlayerId: string | null;
  teamUpdatedAt: RemoteTimestamp | undefined;
  pregameUpdatedAt: RemoteTimestamp | undefined;
}

export interface OptimisticPregameController {
  pregame: PregameViewModel;
  jerseyHolderPlayerId: string | null;
  project: (actionId: string, action: PregameAction) => PregameProjectionHandle;
  markCommitted: (actionId: string, revision: number) => void;
  reject: (actionId: string) => void;
}

const STRICT_ACTIONS = new Set<PregameAction["type"]>([
  "reorderLineup",
  "setDefenseAssignment",
  "regenerateDefense",
]);

function toMillis(timestamp: RemoteTimestamp | undefined): number {
  if (typeof timestamp === "number") return timestamp;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (
    typeof timestamp === "object" &&
    timestamp !== null &&
    "toMillis" in timestamp &&
    typeof timestamp.toMillis === "function"
  ) {
    return timestamp.toMillis();
  }
  return 0;
}

function toDomain(pregame: PregameViewModel): ProjectedPregame {
  return {
    opponentName: pregame.oppName,
    isHome: pregame.isHome,
    openInning: pregame.openInning,
    activePlayerIds: [...pregame.activeIds],
    lineupPlayerIds: pregame.lineupSlots.filter(
      (playerId): playerId is string => Boolean(playerId),
    ),
    gamePitcherId: pregame.gamePitcher,
    defense: structuredClone(pregame.defense),
    selectedScheduleId: pregame.selectedScheduleId,
    scheduledDate: pregame.scheduledDate,
    timeSlot: pregame.timeSlot,
    field: pregame.field,
    beerDuty: structuredClone(pregame.beerDuty),
    revision: pregame.revision,
  };
}

function toView(pregame: ProjectedPregame): PregameViewModel {
  const activeIds = [...pregame.activePlayerIds];
  const lineup = [...pregame.lineupPlayerIds];
  return {
    seasonId: null,
    oppName: pregame.opponentName,
    isHome: pregame.isHome,
    openInning: pregame.openInning,
    activeIds,
    lineupSlots: [
      ...lineup,
      ...Array(Math.max(0, activeIds.length - lineup.length)).fill(null),
    ],
    gamePitcher: pregame.gamePitcherId,
    defense: pregame.defense,
    revision: pregame.revision,
    selectedScheduleId: pregame.selectedScheduleId ?? null,
    scheduledDate: pregame.scheduledDate ?? null,
    timeSlot: pregame.timeSlot ?? null,
    field: pregame.field ?? null,
    beerDuty: pregame.beerDuty ?? null,
  };
}

function domainPlayers(players: PlayerListItem[]): PregamePlayer[] {
  return players.map((player) => ({
    playerId: player.id,
    name: player.name,
    number: player.num,
    positionPreferences: player.positionPreferences,
    active: player.active,
    revision: player.revision,
  }));
}

function applyProjection(
  base: ProjectedPregame,
  projection: PregameProjection,
  players: PlayerListItem[],
  jersey: string | null,
): ProjectedPregame {
  try {
    const result = reducePregameAction(
      base,
      { jerseyHolderPlayerId: jersey, players: domainPlayers(players) },
      projection.action,
    );
    return { ...result.nextPregame, revision: base.revision };
  } catch {
    return STRICT_ACTIONS.has(projection.action.type)
      ? projection.fallback
      : base;
  }
}

export function useOptimisticPregame({
  scopeKey,
  pregame: authoritativePregame,
  players,
  serverJerseyHolderPlayerId,
  teamUpdatedAt,
  pregameUpdatedAt,
}: OptimisticPregameOptions): OptimisticPregameController {
  const [, render] = useState(0);
  const projectionsRef = useRef<PregameProjection[]>([]);
  const scopeRef = useRef(scopeKey);
  const authoritativeRef = useRef(authoritativePregame);
  const playersRef = useRef(players);
  const jerseyRef = useRef(serverJerseyHolderPlayerId);
  const teamUpdatedAtRef = useRef(teamUpdatedAt);
  const pregameUpdatedAtRef = useRef(pregameUpdatedAt);
  const teamSnapshotRef = useRef({
    key: `${serverJerseyHolderPlayerId}:${toMillis(teamUpdatedAt)}`,
    sequence: 0,
  });
  authoritativeRef.current = authoritativePregame;
  playersRef.current = players;
  jerseyRef.current = serverJerseyHolderPlayerId;
  teamUpdatedAtRef.current = teamUpdatedAt;
  pregameUpdatedAtRef.current = pregameUpdatedAt;
  const teamKey = `${serverJerseyHolderPlayerId}:${toMillis(teamUpdatedAt)}`;
  if (teamSnapshotRef.current.key !== teamKey) {
    teamSnapshotRef.current = {
      key: teamKey,
      sequence: teamSnapshotRef.current.sequence + 1,
    };
  }

  useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    const pending = projectionsRef.current;
    projectionsRef.current = [];
    pending.forEach((projection) => projection.resolveAcknowledgement());
    render((value) => value + 1);
  }, [scopeKey]);

  const reconcile = useCallback(() => {
    const currentPregame = authoritativeRef.current;
    const currentJersey = jerseyRef.current;
    const teamSequence = teamSnapshotRef.current.sequence;
    let changed = false;
    const remaining: PregameProjection[] = [];
    for (const [index, projection] of projectionsRef.current.entries()) {
      if (
        projection.committedRevision !== null &&
        currentPregame.revision >= projection.committedRevision
      ) {
        if (!projection.pregameAcknowledged) changed = true;
        projection.pregameAcknowledged = true;
      }
      if (!projection.projectsJersey) {
        projection.teamAcknowledged = true;
      } else if (projection.committedRevision !== null) {
        const exact = currentJersey === projection.jersey;
        const superseded =
          projection.pregameAcknowledged &&
          toMillis(pregameUpdatedAtRef.current) > 0 &&
          toMillis(teamUpdatedAtRef.current) >
            toMillis(pregameUpdatedAtRef.current);
        const supersededAtLaterRevision =
          projection.committedRevision !== null &&
          currentPregame.revision > projection.committedRevision &&
          toMillis(pregameUpdatedAtRef.current) > 0 &&
          toMillis(teamUpdatedAtRef.current) ===
            toMillis(pregameUpdatedAtRef.current);
        const legacySuperseded =
          projection.pregameAcknowledged &&
          toMillis(pregameUpdatedAtRef.current) === 0 &&
          projection.responseTeamSequence !== null &&
          teamSequence > projection.responseTeamSequence;
        const supersededByLaterProjection = projectionsRef.current
          .slice(index + 1)
          .some(
            (later) =>
              later.projectsJersey &&
              later.committedRevision !== null &&
              currentPregame.revision >= later.committedRevision &&
              currentJersey === later.jersey,
          );
        if (
          exact ||
          superseded ||
          supersededAtLaterRevision ||
          legacySuperseded ||
          supersededByLaterProjection
        ) {
          if (!projection.teamAcknowledged) changed = true;
          projection.teamAcknowledged = true;
        }
      }
      if (projection.pregameAcknowledged && projection.teamAcknowledged) {
        projection.resolveAcknowledgement();
        changed = true;
      } else {
        remaining.push(projection);
      }
    }
    if (remaining.length !== projectionsRef.current.length) {
      projectionsRef.current = remaining;
    }
    if (changed) render((value) => value + 1);
  }, []);

  useEffect(reconcile, [
    authoritativePregame,
    serverJerseyHolderPlayerId,
    teamKey,
    reconcile,
  ]);

  let projected = toDomain(authoritativePregame);
  let projectedJersey = serverJerseyHolderPlayerId;
  for (const projection of projectionsRef.current) {
    if (!projection.pregameAcknowledged) {
      projected = applyProjection(
        projected,
        projection,
        players,
        projectedJersey,
      );
    }
    if (projection.projectsJersey && !projection.teamAcknowledged) {
      projectedJersey = projection.jersey;
    }
  }
  const project = useCallback(
    (actionId: string, action: PregameAction): PregameProjectionHandle => {
      try {
        let currentPregame = toDomain(authoritativeRef.current);
        let currentJersey = jerseyRef.current;
        for (const pending of projectionsRef.current) {
          if (!pending.pregameAcknowledged) {
            currentPregame = applyProjection(
              currentPregame,
              pending,
              playersRef.current,
              currentJersey,
            );
          }
          if (pending.projectsJersey && !pending.teamAcknowledged) {
            currentJersey = pending.jersey;
          }
        }
        const result = reducePregameAction(
          currentPregame,
          {
            jerseyHolderPlayerId: currentJersey,
            players: domainPlayers(playersRef.current),
          },
          action,
        );
        let resolveAcknowledgement!: () => void;
        const acknowledgement = new Promise<void>((resolve) => {
          resolveAcknowledgement = resolve;
        });
        const projectsJersey =
          !!result.teamEffects && "jerseyHolderPlayerId" in result.teamEffects;
        const projection: PregameProjection = {
          actionId,
          action,
          fallback: {
            ...result.nextPregame,
            revision: currentPregame.revision,
          },
          jersey: projectsJersey
            ? (result.teamEffects.jerseyHolderPlayerId ?? null)
            : currentJersey,
          projectsJersey,
          committedRevision: null,
          responseTeamSequence: null,
          pregameAcknowledged: false,
          teamAcknowledged: !projectsJersey,
          acknowledgement,
          resolveAcknowledgement,
        };
        projectionsRef.current = [...projectionsRef.current, projection];
        render((value) => value + 1);
        return { acknowledgement };
      } catch {
        return { acknowledgement: null };
      }
    },
    [],
  );

  const markCommitted = useCallback(
    (actionId: string, revision: number) => {
      const projection = projectionsRef.current.find(
        (candidate) => candidate.actionId === actionId,
      );
      if (!projection) return;
      projection.committedRevision = revision;
      projection.responseTeamSequence = teamSnapshotRef.current.sequence;
      reconcile();
    },
    [reconcile],
  );

  const reject = useCallback((actionId: string) => {
    const projection = projectionsRef.current.find(
      (candidate) => candidate.actionId === actionId,
    );
    if (!projection) return;
    projectionsRef.current = projectionsRef.current.filter(
      (candidate) => candidate.actionId !== actionId,
    );
    projection.resolveAcknowledgement();
    render((value) => value + 1);
  }, []);

  return {
    pregame: {
      ...toView(projected),
      seasonId: authoritativePregame.seasonId,
    },
    jerseyHolderPlayerId: projectedJersey,
    project,
    markCommitted,
    reject,
  };
}
